import { TAdFoxBannerCompleteSizes } from '@cian/adfox-component';
import { Label, UIHeading2, UIHeading4 } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { FC, useEffect, useRef } from 'react';

import { Marquee } from 'shared/components/Marquee';

import { TSpecialProjectBannerAnimateContext } from '../../types';

import styles from './SpecialProjectBannerAnimated.css';

type TSpecialProjectBannerAnimatedProps = {
  sizes: TAdFoxBannerCompleteSizes;
  context: TSpecialProjectBannerAnimateContext;
};

export const SpecialProjectBannerAnimated: FC<TSpecialProjectBannerAnimatedProps> = ({ context, sizes }) => {
  const { title, text, url, buttonText, substrateNotNeeded, substrateLogoNotNeeded, disclaimer, logo, bgImage, label } =
    context;

  const containerRef = useRef<HTMLAnchorElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (backgroundRef.current) {
      backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
    }

    const updateParallax = () => {
      if (!containerRef.current || !backgroundRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      const elementCenter = elementTop + elementHeight / 2;
      const viewportCenter = windowHeight / 2;

      if (elementTop < windowHeight && elementTop + elementHeight > 0) {
        const distanceFromCenter = (elementCenter - viewportCenter) / windowHeight;
        const parallaxOffset = distanceFromCenter * 80;
        backgroundRef.current.style.transform = `translate3d(0px, ${parallaxOffset}px, 0px)`;
      } else {
        backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateParallax();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <div className={styles['block-wrapper']} data-testid="SpecialProjectBannerAnimated">
      <a
        className={clsx(
          styles['container'],
          !Number(substrateNotNeeded) && styles['container--without-substrate'],
          !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
        )}
        href={url}
        ref={containerRef}
        rel="nofollow noopener noreferrer"
        style={sizes}
        target="_blank"
      >
        <div className={styles['background-layer']} ref={backgroundRef}>
          <img alt="image" className={styles['background-image']} data-testid="background-image" src={bgImage} />
        </div>
        <div className={styles['wrapper']}>
          <div className={styles['logo']}>
            <img alt={title} className={styles['logo-image']} data-testid="logo" src={logo} />
          </div>
          <div className={styles['content']}>
            <div className={styles['info']}>
              <div className={styles['title']} data-testid="title">
                {title}
              </div>
              <div className={styles['text']}>
                <UIHeading2 color="text-inverted-default" data-testid="text">
                  {text}
                </UIHeading2>
              </div>
            </div>

            <div className={styles['control']}>
              {!!label && (
                <div className={styles['control-label-wrapper']}>
                  <Label background="overlay-default" color="text-inverted-default" data-testid="label">
                    {label}
                  </Label>
                </div>
              )}

              {!!buttonText && (
                <div className={styles['action-button-wrapper']}>
                  <button className={styles['action-button']} data-testid="action-button" type="button">
                    <span className={styles['action-button-text']}>
                      <UIHeading4 color="text-inverted-default">{buttonText}</UIHeading4>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {disclaimer && (
          <div className={styles['note']}>
            <Marquee text={disclaimer} textPadding="s" />
          </div>
        )}
      </a>
    </div>
  );
};
